import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Grid from '@mui/material/Grid';
import { Typography, Card, CardMedia, CardContent, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import Main from 'layouts/Main';
import Container from 'components/Container';

const StudentCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 12px 30px rgba(255, 255, 255, 0.2)' 
      : '0 12px 30px rgba(0, 0, 0, 0.2)',
  },
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
  borderRadius: 16,
  overflow: 'visible',
  position: 'relative',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.3)' 
    : '2px solid rgba(100, 150, 200, 0.4)',
}));

const ImageWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: '120%',
  overflow: 'visible',
  borderRadius: '12px',
  margin: '12px',
  background: `linear-gradient(135deg, ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(100,150,200,0.05)'} 0%, ${theme.palette.mode === 'dark' ? 'rgba(100,150,200,0.1)' : 'rgba(200,220,255,0.1)'} 100%)`,
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.4)' 
    : '2px solid rgba(100, 150, 200, 0.3)',
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const HeaderSection = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
    : 'linear-gradient(135deg, #c5e1ff 0%, #e8d5f2 50%, #d0f0ff 100%)',
  padding: '50px 20px',
  marginBottom: '50px',
  borderRadius: 0,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.6)'
    : '0 8px 32px rgba(0, 0, 0, 0.12)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'dark'
      ? 'radial-gradient(circle at 20% 50%, rgba(100, 150, 200, 0.1) 0%, transparent 50%)'
      : 'radial-gradient(circle at 20% 50%, rgba(100, 150, 200, 0.15) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: '50px',
  borderRadius: '12px',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.3)' 
    : '2px solid rgba(100, 150, 200, 0.4)',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 24px rgba(0, 0, 0, 0.4)'
    : '0 8px 24px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? '#2a3f5f' 
      : '#1565c0',
    color: theme.palette.mode === 'dark' 
      ? '#ffffff' 
      : '#ffffff',
    fontWeight: 800,
    fontSize: '0.95rem',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#3a5f7f' : '#1565c0'}`,
    padding: '16px',
    textAlign: 'center',
  },
  '& .MuiTableCell-body': {
    fontSize: '0.9rem',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#3a5f7f' : '#e0e0e0'}`,
    padding: '14px',
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa',
    textAlign: 'center',
    color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#1a1a1a',
  },
  '& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body': {
    backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#f0f4f8',
  },
}));

const TableTitleBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '30px',
  marginTop: '40px',
}));

const PerformanceCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1a3a52 0%, #1e2d42 100%)'
    : 'linear-gradient(135deg, #e3f2fd 0%, #e8d5f2 50%, #d0f0ff 100%)',
  padding: '30px 20px',
  borderRadius: '16px',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.4)' 
    : '2px solid rgba(100, 150, 200, 0.3)',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 24px rgba(0, 0, 0, 0.4)'
    : '0 8px 24px rgba(0, 0, 0, 0.1)',
  margin: '40px auto',
  maxWidth: '600px',
}));

const Class12Results2026 = () => {
  const theme = useTheme();
  const [config, setConfig] = useState({ base_image_url_2025: '' });

  useEffect(() => {
    fetch('/config.json')
      .then(response => response.json())
      .then(data => setConfig(data))
      .catch(error => console.error('Error fetching config:', error));
  }, []);

  const baseUrl = config.base_image_url_2025;

  const subjectWiseData = [
    { subject: 'ENGLISH', highestScore: 100, noOfStudents: 3, toppers: 'M SHIFAN KHAN, SARA KRSHNAA MANNATH, SOHANI SARIN' },
    { subject: 'MATHS', highestScore: 99, noOfStudents: 2, toppers: 'M SHIFAN KHAN, RANVIR CHOUDHARY' },
    { subject: 'PHYSICS', highestScore: 98, noOfStudents: 1, toppers: 'M SHIFAN KHAN' },
    { subject: 'CHEMISTRY', highestScore: 99, noOfStudents: 1, toppers: 'M SHIFAN KHAN' },
    { subject: 'BIOLOGY', highestScore: 99, noOfStudents: 1, toppers: 'M SHIFAN KHAN' },
    { subject: 'COMPUTER SCIENCE', highestScore: 100, noOfStudents: 1, toppers: 'KAUSHAL SURESH' },
    { subject: 'ECONOMICS', highestScore: 99, noOfStudents: 1, toppers: 'SARA KRSHNAA MANNATH' },
    { subject: 'BUSINESS STUDIES', highestScore: 99, noOfStudents: 1, toppers: 'RIDHIMAA LAKHANI' },
    { subject: 'ACCOUNTACY', highestScore: 98, noOfStudents: 1, toppers: 'RIDHIMAA LAKHANI' },
    { subject: 'APPLIED MATH', highestScore: 97, noOfStudents: 1, toppers: 'SARA KRSHNAA MANNATH' },
    { subject: 'INFORMATICS PRACTICES', highestScore: 100, noOfStudents: 2, toppers: 'SOHANI SARIN, DISHA BHAGWAT' },
    { subject: 'HISTORY', highestScore: 96, noOfStudents: 1, toppers: 'DHANANYA KODKANI' },
    { subject: 'POLITICAL SCIENCE', highestScore: 99, noOfStudents: 1, toppers: 'DISHA BHAGWAT' },
    { subject: 'PHYSICAL EDUCATION', highestScore: 97, noOfStudents: 1, toppers: 'NUHA TAMEEM K' },
  ];

  const performanceData = [
    { range: 'above 90%', percentage: 29.2 },
    { range: '80%-90%', percentage: 31.9 },
    { range: '70%-80%', percentage: 30.6 },
    { range: '63%-70%', percentage: 8.3 },
  ];

  const students = [
    { id: 1, name: 'M SHIFAN KHAN', stream: 'SCIENCE - PCMB', percentage: 99, image: `${baseUrl}/home/achievements2026/class12-results-2026/1.webp` },
    { id: 2, name: 'SOHANI SARIN', stream: 'COMMERCE - EABsIP', percentage: 98.6, image: `${baseUrl}/home/achievements2026/class12-results-2026/2.webp` },
    { id: 3, name: 'KAUSHAL SURESH', stream: 'SCIENCE - PCMCs', percentage: 97.6, image: `${baseUrl}/home/achievements2026/class12-results-2026/3.webp` },
    { id: 4, name: 'RANVIR CHOUDHARY', stream: 'SCIENCE - PCMCs', percentage: 97, image: `${baseUrl}/home/achievements2026/class12-results-2026/4.webp` },
    { id: 5, name: 'DISHA BHAGWAT', stream: 'HUMANITIES - HPEIp', percentage: 96.8, image: `${baseUrl}/home/achievements2026/class12-results-2026/5.webp` },
    { id: 6, name: 'SARA KRSHNAA MANNATH', stream: 'HUMANITIES - HPEM', percentage: 96.6, image: `${baseUrl}/home/achievements2026/class12-results-2026/6.webp` },
    { id: 7, name: 'RIDHIMAA LAKHANI', stream: 'COMMERCE - EABsM', percentage: 95.6, image: `${baseUrl}/home/achievements2026/class12-results-2026/7.webp` },
    { id: 8, name: 'SANAVI TANGA', stream: 'HUMANITIES - HPEIp', percentage: 94.6, image: `${baseUrl}/home/achievements2026/class12-results-2026/8.webp` },
    { id: 9, name: 'DHANANYA KODKANI', stream: 'HUMANITIES - HPEPe', percentage: 94.4, image: `${baseUrl}/home/achievements2026/class12-results-2026/9.webp` },
    { id: 10, name: 'SAJEEV SIVASANKAR', stream: 'SCIENCE - PCMCs', percentage: 94.2, image: `${baseUrl}/home/achievements2026/class12-results-2026/10.webp` },
    { id: 11, name: 'KHAVIN SELVAN KUMARAN', stream: 'SCIENCE - PCMCs', percentage: 93.8, image: `${baseUrl}/home/achievements2026/class12-results-2026/11.webp` },
    { id: 12, name: 'OHILA Y', stream: 'COMMERCE - EABsM', percentage: 93.4, image: `${baseUrl}/home/achievements2026/class12-results-2026/12.webp` },
    { id: 13, name: 'ADITHI KRITHIVASAN', stream: 'HUMANITIES - HPEPe', percentage: 92.6, image: `${baseUrl}/home/achievements2026/class12-results-2026/13.webp` },
    { id: 14, name: 'CHINMAY PRAVEEN', stream: 'SCIENCE - PCMCs', percentage: 92.6, image: `${baseUrl}/home/achievements2026/class12-results-2026/14.webp` },
    { id: 15, name: 'K C SHREYAS', stream: 'SCIENCE - PCMB', percentage: 92.4, image: `${baseUrl}/home/achievements2026/class12-results-2026/15.webp` },
    { id: 16, name: 'AARON JACOB', stream: 'SCIENCE - PCMCs', percentage: 91.8, image: `${baseUrl}/home/achievements2026/class12-results-2026/16.webp` },
    { id: 17, name: 'PRADHYUMNA M', stream: 'COMMERCE - EABsIp', percentage: 91.8, image: `${baseUrl}/home/achievements2026/class12-results-2026/20.webp` },
    { id: 18, name: 'AKANKSHA PATRO', stream: 'SCIENCE - PCME', percentage: 91, image: `${baseUrl}/home/achievements2026/class12-results-2026/21.webp` },
    { id: 19, name: 'DANISH MOHAMAD UMAR DUSGI', stream: 'SCIENCE - PCMCs', percentage: 90.8, image: `${baseUrl}/home/achievements2026/class12-results-2026/17.webp` },
    { id: 20, name: 'RASHIKA AGARWAL', stream: 'COMMERCE - EABsM', percentage: 90.8, image: `${baseUrl}/home/achievements2026/class12-results-2026/18.webp` },
    { id: 21, name: 'RITAPRABHA SIL', stream: 'SCIENCE - PCMCs', percentage: 90.4, image: `${baseUrl}/home/achievements2026/class12-results-2026/19.webp` },
  ];

  const rowConfig = [
    { cols: 3, students: students.slice(0, 3) },
    { cols: 4, students: students.slice(3, 7) },
    { cols: 4, students: students.slice(7, 11) },
    { cols: 4, students: students.slice(11, 15) },
    { cols: 3, students: students.slice(15, 18) },
    { cols: 3, students: students.slice(18, 21) },
  ];

  return (
    <Main>
      <Helmet>
        <title>NPS YPR CBSE Result 2026 - Class 12</title>
      </Helmet>
        <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#fafafa' }}>
      {/* Header Section */}
      <HeaderSection elevation={0}>
        <Box sx={{ textAlign: 'center', mb: 2, position: 'relative', zIndex: 1 }}>
        
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.4rem' },
              textShadow: theme.palette.mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none'
            }}
          >
            CBSE Results 2026 - Class 12
          </Typography>
         
          <Typography 
            variant="h6" 
            align={'justify'}
          >
            Congratulations to our Class 12 students who have achieved excellent results in the CBSE board examinations 2025-26, through perseverance and dedication. This achievement is a testimony to the collective effort, guidance and support of our students, mentors and parents.
          </Typography>
        </Box>
      </HeaderSection>

      {/* Students Grid - School Toppers */}
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 6 }, pb: 8 }}>
        <TableTitleBox sx={{ mt: 0 }}>
           <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }
            }}
          >
            School Toppers
          </Typography>
        </TableTitleBox>
        {rowConfig.map((row, rowIndex) => (
          <Grid 
            container 
            spacing={{ xs: 1.5, sm: 2, md: 3, lg: 4 }} 
            sx={{ 
              mb: 5, 
              justifyContent: 'center',
              display: 'flex',
              width: '100%'
            }}
            key={rowIndex}
          >
            {row.students.map((student) => (
              <Grid 
                item 
                xs={row.cols === 2 ? 12 : row.cols === 3 ? 12 : row.cols === 4 ? 6 : 12}
                sm={row.cols === 2 ? 6 : row.cols === 3 ? 4 : 3}
                md={12 / row.cols}
                key={student.id}
                sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start'
                }}
              >
                <StudentCard sx={{ width: '100%', maxWidth: { xs: '180px', sm: '200px', md: '220px', lg: '240px' } }}>
                  <ImageWrapper>
                    <img
                      src={student.image}
                      alt={student.name}
                      loading="lazy"
                    />
                  </ImageWrapper>
                  <CardContent sx={{ pb: 2.5, pt: 2, px: 1.5, textAlign: 'center' }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 800, 
                        mb: 1.5,
                        fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
                        color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a2e',
                        lineHeight: 1.2,
                        letterSpacing: '0.5px',
                      }}
                    >
                      {student.name}
                    </Typography>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          color: theme.palette.mode === 'dark' ? '#aaa' : '#666',
                          mb: 0.6,
                          fontWeight: 700,
                          display: 'block',
                          letterSpacing: '0.4px'
                        }}
                      >
                        {student.stream}
                      </Typography>
                      <Typography 
                        sx={{ 
                          fontWeight: 800, 
                          color: theme.palette.primary.main,
                          fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1.1rem' }
                        }}
                      >
                        {student.percentage}%
                      </Typography>
                    </Box>
                  </CardContent>
                </StudentCard>
              </Grid>
            ))}
          </Grid>
        ))}
      </Box>

      {/* Subject Wise Highest Score Table */}
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 6 } }}>
        <TableTitleBox>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }
            }}
          >
            Subject Wise Highest Score
          </Typography>
        </TableTitleBox>

        <StyledTableContainer>
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell>SUBJECT</TableCell>
                <TableCell>HIGHEST SCORE</TableCell>
                <TableCell>NO. OF STUDENTS</TableCell>
                <TableCell>NAME OF THE TOPPERS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subjectWiseData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.subject}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{row.highestScore}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.noOfStudents}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem', textAlign: 'left' }}>{row.toppers}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        {/* Overall Performance Section */}
        <TableTitleBox sx={{ mt: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }
            }}
          >
            Overall Performance
          </Typography>
        </TableTitleBox>

        <PerformanceCard elevation={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {performanceData.map((item, index) => (
              <Box 
                key={index}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: index < performanceData.length - 1 ? '15px' : '0',
                  borderBottom: index < performanceData.length - 1 ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(100, 150, 200, 0.2)' : 'rgba(100, 150, 200, 0.3)'}` : 'none'
                }}
              >
                <Typography 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                    color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#1a1a1a',
                  }}
                >
                  {item.percentage}% of students scored {item.range}
                </Typography>
              </Box>
            ))}
          </Box>
        </PerformanceCard>
      </Box>
     </Box>
    </Main>
  );
};

export default Class12Results2026
